import { FC, HTMLAttributes, useCallback, useContext, memo, MouseEventHandler } from 'react'
import Link from 'next/link'
import tw from 'tailwind-styled-components'
import Image from 'next/image'
import { DateTime } from 'luxon'
import { useTranslation } from 'next-i18next'
import { Tooltip } from '@material-ui/core'
import {
  ListAuctionFragment,
  useBaseAuctionChangedSubscription,
  useFollowAuctionMutation,
  useUnfollowAuctionMutation,
} from '@foros-fe/graphql'
import { ActiveTag } from './ActiveTag'
import { SvgIcon } from './SvgIcon'
import { Countdown } from './Countdown'
import { clientOnlyModule, formatMoney, endTimestamp } from '../utils'
import AuctionListContext from '../contexts/AuctionList'
import Pin from '@foros-fe/assets/icons/pin.png'
import SpeciesIcon from '@foros-fe/assets/icons/species.png'
import TypeIcon from '@foros-fe/assets/icons/type.png'
import Star from '@foros-fe/assets/icons/star.png'
import StarEmpty from '@foros-fe/assets/icons/star-empty.png'
import ArrowRight from '@foros-fe/assets/icons/card-chevron.svg'
import ConfirmedByForos from '@foros-fe/assets/icons/confirmed.svg'

const AuctionCardMap = clientOnlyModule(
  () => import('@foros-fe/core/components/Maps/AuctionCardMap')
)

type Props = Omit<HTMLAttributes<HTMLDivElement>, 'id' | 'title'> & ListAuctionFragment

const Line = tw.div`flex flex-1`
const LineContainer = tw(Line)`items-center`
const CardContainer = tw(Line)<{ $selected: boolean }>`
  flex-col
  md:flex-row
  rounded-md
  bg-white
  filter
  overflow-hidden
  transition-shadow
  duration-300
  group
  ${({ $selected }) => ($selected ? 'shadow-hover z-10' : 'shadow')}
`
const TextContainer = tw(Line)`
  flex
  flex-1
  flex-col
`
const InfoContainer = tw(Line)`
  flex
  flex-1
  p-4
  relative
`
const MapContainer = tw.div`
  flex
  items-start
  justify-start
  min-w-[200px]
  min-h-[200px]
  h-full
  relative
  z-10
`
const PriceContainer = tw.div`min-w-[150px] flex flex-col justify-start`
const PriceTitle = tw.div`text-gray-700 text-xs uppercase mb-1 whitespace-nowrap`
const Price = tw.div`text-black font-poppins text-lg`
const SmallText = tw.p`text-gray-700 text-sm`
const StatusTag = tw(ActiveTag)`absolute z-[400] top-3 left-3`
const ConfirmedContainer = tw.div`
  absolute
  bottom-0
  left-0
  right-0
  bg-black/50
  flex
  items-center
  justify-center
  h-8
  z-[400]
`

export const AuctionCard: FC<Props> = memo(
  ({
    id,
    humanId,
    title,
    address,
    endTime,
    startingBid,
    highestBidObject,
    followed,
    owner,
    cadastralPlots,
    forestType,
    salePurpose,
    forestInspected,
    ...props
  }) => {
    useBaseAuctionChangedSubscription({ variables: { id } })
    const { t } = useTranslation(['auctions', 'enum', 'forest'])
    const [followAuctionMutation] = useFollowAuctionMutation()
    const [unfollowAuctionMutation] = useUnfollowAuctionMutation()
    const { selected, setSelected, currentUserId } = useContext(AuctionListContext)

    const selectAuction = useCallback(() => setSelected(id), [setSelected, id])
    const deselectAuction = useCallback(() => setSelected(null), [setSelected])
    const onFollowClick = useCallback<MouseEventHandler>(
      async (e) => {
        e.preventDefault()

        const variables = { input: { auction: { id } } }

        if (followed) {
          await unfollowAuctionMutation({ variables })
        } else {
          await followAuctionMutation({ variables })
        }
      },
      [followed, id, unfollowAuctionMutation, followAuctionMutation]
    )
    const active = DateTime.fromISO(endTime) > DateTime.local()

    return (
      <Link href={`/auctions/${id}`}>
        <a className='flex'>
          <CardContainer
            {...props}
            $selected={selected === id}
            onMouseEnter={selectAuction}
            onMouseLeave={deselectAuction}
          >
            <MapContainer>
              <AuctionCardMap cadastralPlots={cadastralPlots} />
              <StatusTag
                active={active}
                title={active ? t('common:auction.active') : t('common:auction.finished')}
              />
              {forestInspected ? (
                <ConfirmedContainer>
                  <SvgIcon
                    src={ConfirmedByForos}
                    inheritColors
                    useFill
                    size={16}
                    className='mr-2 text-white'
                  />
                  <p className='text-white text-sm'>{t('inspectedByForesters')}</p>
                </ConfirmedContainer>
              ) : null}
            </MapContainer>
            <InfoContainer>
              <TextContainer>
                <Line className='flex-col md:flex-row mb-2 items-start md:items-center'>
                  <p className='mr-4 mb-2 md:mb-auto font-poppins font-bold text-lg'>{title}</p>
                  <p className='text-xs text-gray-700'>#{humanId}</p>
                </Line>
                <LineContainer className='mb-2 items-center space-x-2'>
                  <LineContainer>
                    <div className='flex mr-3 min-w-[24px]'>
                      <Image src={Pin} width={24} height={24} />
                    </div>
                    <SmallText>{address}</SmallText>
                  </LineContainer>
                  <LineContainer>
                    <div className='flex mr-3 min-w-[24px]'>
                      <Image src={TypeIcon} width={24} height={24} />
                    </div>
                    <SmallText className='flex-1'>
                      {t(`enum:AuctionSalePurpose.${salePurpose}`)}
                    </SmallText>
                  </LineContainer>
                </LineContainer>
                <LineContainer className='items-center space-x-2'>
                  <Countdown endTime={endTime} />
                  <LineContainer>
                    <div className='flex mr-3 min-w-[24px]'>
                      <Image src={SpeciesIcon} width={24} height={24} />
                    </div>
                    <SmallText>
                      {t([`enum:ForestType.${forestType}`, 'forest:notSpecified'])}
                    </SmallText>
                  </LineContainer>
                </LineContainer>
                <Line className='mb-5 pl-9 text-gray-700 text-sm'>{endTimestamp(endTime)}</Line>
                <Line>
                  <PriceContainer className='mr-5 '>
                    <PriceTitle>{t('common:startingPrice')}</PriceTitle>
                    <Price>{formatMoney(startingBid)}</Price>
                  </PriceContainer>
                  <PriceContainer>
                    <PriceTitle>{t('common:highestBid')}</PriceTitle>
                    <Price className='font-semibold'>{formatMoney(highestBidObject?.price)}</Price>
                  </PriceContainer>
                </Line>
              </TextContainer>
              <div className='flex flex-col justify-center items-center h-full w-[20px] overflow-visible hover:text-gray-700'>
                {currentUserId && currentUserId !== owner?.id && active && (
                  <div
                    className='absolute z-20 top-4 right-4 cursor-pointer'
                    onClick={onFollowClick}
                  >
                    <Tooltip
                      title={(followed ? t('unfollow') : t('follow')) as string}
                      placement='top'
                      arrow
                    >
                      <div>
                        <Image src={followed ? Star : StarEmpty} width={20} height={20} />
                      </div>
                    </Tooltip>
                  </div>
                )}
                <SvgIcon
                  inheritColors
                  src={ArrowRight}
                  width={13}
                  height={22}
                  className='text-gray-500 group-hover:text-gray-700 p-2 -mr-2'
                />
              </div>
            </InfoContainer>
          </CardContainer>
        </a>
      </Link>
    )
  }
)
